import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Headset, Send } from 'lucide-react';
import { UserRole, WS_EVENT, type SupportMessagePayload } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { sonarAviso } from '@/lib/sonido';
import { useEventoSocket } from '@/lib/socket';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface MensajeSoporte {
  id: string;
  senderId: string;
  senderRole: UserRole;
  body: string;
  readAt: string | null;
  createdAt: string;
}

/**
 * Soporte para el comercio.
 *
 * La misma conversación que tiene un cliente con administración, pero desde
 * el panel: una sola, para siempre, como un chat. Del otro lado,
 * administración ve de qué comercio viene y quién escribió.
 */
export function SoporteComercio() {
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [texto, setTexto] = useState('');
  const final = useRef<HTMLDivElement>(null);

  const mensajes = useQuery({
    queryKey: ['soporte', 'mensajes'],
    queryFn: async () => (await api.get<MensajeSoporte[]>('/soporte/chat')).data,
  });

  const enviar = useMutation({
    mutationFn: async (body: string) =>
      (await api.post<MensajeSoporte>('/soporte/chat', { body })).data,
    onSuccess: () => {
      setTexto('');
      void cliente.invalidateQueries({ queryKey: ['soporte'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  useEventoSocket<SupportMessagePayload>(WS_EVENT.SUPPORT_MESSAGE, (mensaje) => {
    void cliente.invalidateQueries({ queryKey: ['soporte'] });
    if (mensaje.senderRole === UserRole.ADMIN) sonarAviso();
  });

  useEffect(() => {
    final.current?.scrollIntoView({ block: 'end' });
  }, [mensajes.data]);

  // Pedir la conversación ya la marca leída en el servidor; el menú se entera aquí.
  useEffect(() => {
    if (!mensajes.data) return;
    void cliente.invalidateQueries({ queryKey: ['soporte', 'sin-leer'] });
  }, [mensajes.data, cliente]);

  const lista = mensajes.data ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-3xl text-kick-dark">Soporte</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Escríbele a administración de Kick. Te respondemos aquí mismo y te avisamos con un sonido.
      </p>

      <div className="mt-6 flex h-[min(34rem,70vh)] flex-col overflow-hidden rounded-kick bg-white shadow-card">
        <header className="flex items-center gap-3 bg-kick-dark px-4 py-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white">
            <Headset className="h-5 w-5" />
          </span>
          <div>
            <p className="font-bold text-white">Soporte de Kick</p>
            <p className="text-xs text-white/60">Te respondemos lo antes posible</p>
          </div>
        </header>

        <div className="flex-1 space-y-2 overflow-y-auto bg-kick-light p-4">
          {mensajes.isLoading && <p className="text-center text-sm text-kick-gray">Cargando…</p>}

          {!mensajes.isLoading && lista.length === 0 && (
            <div className="px-2 py-6 text-center">
              <p className="text-sm text-kick-gray">
                Cuéntanos qué necesitas. Si es por un pedido concreto, dinos el código —empieza por
                KD— y lo miramos de una.
              </p>
            </div>
          )}

          {lista.map((m) => {
            const mio = m.senderRole !== UserRole.ADMIN;
            return (
              <div key={m.id} className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] rounded-kick px-3 py-2 text-sm',
                    mio ? 'bg-kick-red text-white' : 'bg-white text-kick-ink',
                  )}
                >
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <p className={cn('mt-0.5 text-[11px]', mio ? 'text-white/70' : 'text-kick-gray')}>
                    {new Date(m.createdAt).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'America/Bogota',
                    })}
                  </p>
                </div>
              </div>
            );
          })}

          <div ref={final} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (texto.trim()) enviar.mutate(texto.trim());
          }}
          className="flex gap-2 border-t border-kick-gray/15 p-3"
        >
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            maxLength={1000}
            placeholder="Escribe tu mensaje…"
            aria-label="Mensaje para soporte"
            className="min-w-0 flex-1 rounded-kick border border-kick-gray/30 px-3 py-2.5 text-sm text-kick-ink outline-none transition focus:border-kick-red"
          />
          <Boton
            type="submit"
            className="px-3"
            disabled={!texto.trim()}
            cargando={enviar.isPending}
            icono={<Send className="h-4 w-4" />}
          >
            Enviar
          </Boton>
        </form>
      </div>
    </div>
  );
}
